<template>
  <parent>
    <loading-page v-if="gettingCertificate" />
    <form-wrapper
      v-else
      class="request-form"
    >
      <template v-slot:body>
        <validation-observer
          ref="certificateRules"
          tag="form"
        >
          <paper-simple>
            <custom-input-radio
              v-model="certificate.certificateIssuerType"
              label="Select certificate issuer"
              :options="certificateIssuerOptions"
            />
          </paper-simple>

          <paper-simple>
            <custom-input-radio
              v-model="selectVerificationMethod"
              label="Select validation method"
              :options="verificationMethodOptions"
            />
            <div
              v-if="autovalidatableDomains && autovalidatableDomains.length > 0"
              class="d-flex align-items-start"
            >
              <label class="mr-75 mb-0">Auto-validatable Domains:</label>
              <div class="d-flex flex-wrap">
                <div
                  v-if="autovalidatableDomains && autovalidatableDomains.length > 0"
                  class="d-flex flex-wrap"
                >
                  <span
                    v-for="(domain, index) in autovalidatableDomains"
                    :key="index"
                    class="mr-50 mb-50 badge badge-primary"
                    style="font-size: 0.75rem;"
                  >
                    {{ domain }}
                  </span>
                </div>
              </div>
            </div>
          </paper-simple>

          <paper-simple>
            <validation-provider
              #default="{ errors }"
              name="Domain Name"
              rules="required|wildcard"
            >
              <b-form-group
                label="Domain Name"
                label-for="name"
                invalid-feedback="The Domain Name field is required"
                :state="errors.length > 0 ? false:null"
              >
                <b-form-input
                  id="name"
                  v-model="certificate.domainName"
                  :state="errors.length > 0 ? false:null"
                  placeholder="Add domain name here..."
                />
              </b-form-group>
            </validation-provider>
            <template v-for="name in domainNames">
              <validation-provider
                :key="name.id"
                #default="{ errors }"
                rules="wildcard"
              >
                <div class="d-flex align-items-center mb-1">
                  <b-input
                    v-model="name.text"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Add domain name here..."
                    class="mb-0"
                  />
                  <a @click="removeDomain(name.id)">
                    <GjIcon name="Delete" />
                  </a>
                </div>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </template>
            <div>
              <a
                class="text-primary text-medium"
                @click="addDomain()"
              >
                + Add another name to this certificate
              </a>
            </div>
          </paper-simple>

          <paper-simple>
            <custom-input-radio
              v-model="certificate.scopeId"
              label="Select scope"
              :options="getObjectOptions(certificateModel.scopeOptions)"
            />
          </paper-simple>

          <paper-simple>
            <div class="my-1">
              <b-form-checkbox
                v-model="certificate.syncOnCdn77"
                switch
              >
                Sync with CDN77
              </b-form-checkbox>
            </div>
          </paper-simple>
        </validation-observer>
      </template>
      <template v-slot:footer>
        <b-button
          variant="bg-white"
          @click="cancel()"
        >
          Cancel
        </b-button>
        <button-loading
          variant="bg-primary"
          class="ml-16"
          :spinning="addingCertificate"
          @click="validationCertificatesSettings()"
        >Confirm
        </button-loading>
      </template>
    </form-wrapper>
  </parent>
</template>
<script>
import CustomInputRadio from '@/components/form/CustomInputRadio.vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, wildcard } from '@validations'
import redirectError from '@/services/redirectError'
import letsEncryptImg from '@/assets/svg/lets-encrypt.svg'
import zeroSslImg from '@/assets/svg/zerossl.svg'

export default {
  name: 'RequestCertificate',
  components: { CustomInputRadio, ValidationProvider, ValidationObserver },
  data() {
    return {
      required,
      wildcard,
      id: 0,
      certificate: {
        domainName: '',
        additionalDomainNames: [],
        scopeId: 0,
        certificateIssuerType: 2,
        syncOnCdn77: false,
      },
      selectVerificationMethod: 0,
      certificateIssuerOptions: [
        {
          value: 1,
          text: '',
          image: letsEncryptImg,
          imageWidth: 'auto',
        },
        {
          value: 2,
          text: '',
          image: zeroSslImg,
          imageWidth: 'auto',
        },
      ],
      verificationMethodOptions: [
        {
          value: 0,
          text: 'DNS validation',
          description: 'Choose this option if you have or can obtain permission to modify the DNS configuration for the domains in your certificate request.',
        },
        {
          value: 1,
          text: 'HTTP validation',
          description: 'Choose this option if you do not have permission or cannot obtain permission to modify the DNS configuration for the domains in your certificate request.',
          disabled: true,
        },
      ],
      domainNames: [],
    }
  },
  computed: {
    lastIndex() {
      return this.domainNames.length ? this.domainNames[this.domainNames.length - 1].id : 0
    },
    showAdditionalNames() {
      return this.domainNames.some(item => item.text !== '')
    },
    selectedVerificationMethod() {
      return this.verificationMethodOptions.find(o => o.value === this.certificate.selectVerificationMethod)?.text
    },
    addingCertificate() {
      return this.$store.state.cdn.addingCertificate
    },
    certificateModel() {
      return this.$store.state.cdn.certificate
    },
    gettingCertificate() {
      return this.$store.state.cdn.gettingCertificate
    },
    autovalidatableDomains() {
      return this.$store.state.cdn.autovalidatableDomains
    },
  },
  created() {
    this.$store.dispatch('cdn/getCertificate', 0)
      .then(() => { this.certificate.scopeId = this.certificateModel.scopeId })
      .catch(err => redirectError(err, this.$router, this.$route.path))

    this.$store.dispatch('cdn/fetchAutoValidatableDomains')
      .catch(error => {
        this.errorToast(error.response?.data?.errors[0] || 'Failed to fetch auto-validatable domains.')
      })
  },
  methods: {
    formSubmitted() {
      this.certificate.additionalDomainNames = []
      for (let i = 0; i < this.domainNames.length; i++) {
        if (this.domainNames[i].text !== null && this.domainNames[i].text.length) {
          this.certificate.additionalDomainNames.push(this.domainNames[i].text)
        }
      }
      this.$store.dispatch('cdn/addCertificate', this.certificate)
        .then(response => {
          this.$router.push({ name: 'certificates-view', params: { certificateId: response.data.result.id } })
        })
        .catch(error => { this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while creating certificate.') })
    },
    validationCertificatesSettings() {
      return new Promise((resolve, reject) => {
        this.$refs.certificateRules.validate().then(success => {
          if (success) {
            this.formSubmitted()
          } else {
            reject()
          }
        })
      })
    },
    cancel() {
      this.$router.push({ name: 'certificates' })
    },
    removeDomain(id) {
      this.domainNames = this.domainNames.filter(item => item.id !== id)
    },
    addDomain() {
      this.domainNames.push(
        { id: this.lastIndex + 1, text: '' },
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.request-form::v-deep {
  .full-form {
    .invisible {
      display: none;
    }
  }
  .form-wrapper {
    background: red;
    &__body {
      > div {
        margin: auto;
      }
    }
  }
}
.lets-encrypt {
  width: 200px;
}

.btn-bg-white {
  &.transparent {
    background-color: transparent;
  }
}
</style>
